// scroll-rails.ts — the grid scroll rails (owner item 67): the transcript
// minimap's rail treatment, generalized into plain proportional scrollbars
// for AG Grid viewports in BOTH clients (the Helm's Glance grid, the Bridge
// home's releases/users/audit grids).
//
// WHY SYNTHETIC, NOT NATIVE-STYLED (the route decision, measured against
// ag-grid-community 36.1.0's dist):
//   - AG 36 scrolls both axes on ONE element, `.ag-grid-viewport`, whose
//     native scrollbars AG hides unconditionally (`scrollbar-width: none
//     !important`); what LOOKS like scrollbars are fake mirror components
//     (`.ag-body-horizontal-scroll` / `.ag-body-vertical-scroll`).
//   - The fake horizontal bar spans the FULL grid width: its container is
//     sized `bodyWidth + leftSticky + rightSticky` (fakeHScroll's
//     setContainerWidth) with no pinned spacers anywhere in the v36 dist —
//     so with pinned columns it scrolls "under" the pinned regions, which
//     is exactly what the owner refused.
//   - Always-visible cannot be won there either: AG's invisibleScrollbar
//     mode is decided by an ENVIRONMENT probe (`_isInvisibleScrollbar`,
//     a classless div measured on body) that ignores the `scrollbarWidth`
//     grid option, so on macOS overlay-scrollbar systems the fake bars ride
//     `ag-apple-scrollbar` fade classes and inline sizes — fighting that is
//     an !important war against inline styles rewritten on a 100ms debounce.
// So: hide the fake mirrors (they are pure UI — scrollFeature reads and
// writes eGridViewport.scrollLeft/Top directly) and draw rails that mirror
// the one real scroll container, exactly like the transcript minimap draws
// over its scroll host.
//
// THE CONTRACT (the owner's words):
//   - the rail spans and scrolls ONLY the scrollable center: with pinned
//     (sticky) columns the horizontal rail is inset by the pinned widths;
//     the vertical rail is inset by the sticky pinned-top/bottom row bands;
//   - the thumb length represents the visible fraction OF THE SCROLLABLE
//     AREA (window/scrollable, not viewport/content);
//   - a rail is VISIBLE whenever its axis actually scrolls (`.on`), never
//     auto-hiding, and absent when there is nothing to scroll;
//   - no overscroll bounce: `overscroll-behavior: none` on the viewport;
//   - (owner 2026-09-07) a rail NEVER covers content: every railed surface
//     reserves a GUTTER the rail lives in, on both axes and in both variants
//     — see the gutter note below RAIL_EDGE.
//
// VISUAL TREATMENT: byte-for-byte the transcript minimap's rail values
// (helm-bridge styles.css `.pi-minimap` block — the two blocks cross-
// reference each other): 12px rail, 6px edge offsets, radius 6, track
// ink-mix 6% -> 10% hover; thumb inset 1px, radius 5, min 14px, accent-mix
// 55% -> 80% hover/drag. Colors ride `--sb-rail-ink` / `--sb-rail-accent`
// with a fallback to the Helm's own tokens (`--ink` / `--accent`), so the
// Helm needs no mapping and the console maps its `--hc-*` pair in ONE
// place (its styles.css :root).
//
// This module deliberately imports NOTHING from ag-grid: the grid API is
// consumed structurally (GridApiLike) and the DOM contract is the four v36
// class names below, pinned by the probe suite.

export interface RailGeomIn {
  /** Viewport client length on this axis (px). */
  viewLen: number;
  /** Content scroll length on this axis (scrollWidth/scrollHeight, px). */
  contentLen: number;
  /** Current scroll position (scrollLeft/scrollTop, px). */
  scrollPos: number;
  /** Sticky band at the start edge (pinned-left width / pinned-top height). */
  stickyStart: number;
  /** Sticky band at the end edge (pinned-right width / pinned-bottom height). */
  stickyEnd: number;
  /** Rail track length (px) — already inset by the sticky bands. */
  railLen: number;
  /** Minimum thumb length (px); default 14 (the minimap's min-height). */
  minThumb?: number;
}

export interface RailGeomOut {
  thumbLen: number;
  thumbPos: number;
  /** Visible fraction of the SCROLLABLE area (what the thumb represents). */
  visibleFrac: number;
  /** Scroll range (contentLen - viewLen) — the drag inverse rides it. */
  range: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** The ONE copy of the rail math (DOM-free so the pure test pins it —
 * the markerRailFraction discipline). Returns null when the axis has no
 * scrollable area: the rail is then HIDDEN, which is the owner's "always
 * visible if there is a scrollable area" read in both directions. */
export function railGeom(a: RailGeomIn): RailGeomOut | null {
  const range = a.contentLen - a.viewLen;
  if (!(range > 0.5) || !(a.railLen > 0)) return null;
  // The scrollable WINDOW: what the viewport shows of the non-sticky
  // content. Sticky bands subtract from both sides of the ratio, so a grid
  // with half its width pinned still reports the truth about the half that
  // moves.
  const winLen = Math.max(1, a.viewLen - a.stickyStart - a.stickyEnd);
  const scrollableLen = Math.max(winLen, a.contentLen - a.stickyStart - a.stickyEnd);
  const visibleFrac = clamp(winLen / scrollableLen, 0, 1);
  const minThumb = a.minThumb ?? 14;
  const thumbLen = clamp(Math.max(minThumb, a.railLen * visibleFrac), minThumb, a.railLen);
  const travel = Math.max(0, a.railLen - thumbLen);
  const thumbPos = range > 0 ? clamp((a.scrollPos / range) * travel, 0, travel) : 0;
  return { thumbLen, thumbPos, visibleFrac, range };
}

/** Drag inverse: a thumb position back to a scroll position. */
export function scrollFromThumb(thumbPos: number, railLen: number, thumbLen: number, range: number): number {
  const travel = railLen - thumbLen;
  if (!(travel > 0) || !(range > 0)) return 0;
  return clamp(thumbPos / travel, 0, 1) * range;
}

// --- the AG Grid adapter -----------------------------------------------

/** The slice of GridApi the rails consume — structural on purpose: this
 * package carries no ag-grid dependency. */
export interface GridApiLike {
  getColumnState(): Array<{ pinned?: 'left' | 'right' | boolean | null; hide?: boolean | null; width?: number | null }>;
  addEventListener(eventType: string, listener: (event: unknown) => void): void;
  removeEventListener(eventType: string, listener: (event: unknown) => void): void;
  isDestroyed?(): boolean;
}

/** The v36 DOM contract (see glance-grid.ts's v36 selector note: these are
 * measured against the shipped dist, and the probe suite pins them). */
const SEL_VIEWPORT = '.ag-grid-viewport';
/** The viewport's parent in the v36 template (`.ag-root > .ag-grid-viewport`,
 * getGridBodyTemplate) — the element that RESERVES the grid strip. */
const SEL_ROOT = '.ag-root';
const SEL_PINNED_TOP = '.ag-grid-pinned-top-rows';
const SEL_PINNED_BOTTOM = '.ag-grid-pinned-bottom-rows';

const RAIL_THICKNESS = 12; // the minimap rail width
const RAIL_EDGE = 6; // the minimap's edge offset

// --- THE GUTTER (owner defect 2026-09-07) ---------------------------------
//
// A rail is drawn INSIDE the surface it measures: `right: edge` + `thickness`
// wide means it owns the last `edge + thickness` px of the pane's border box,
// and the native bar it replaces is hidden — so the content box still runs the
// FULL width and the rail paints on top of it. The owner measured three
// surfaces: the Glance grid (the vertical rail over the last column, the
// horizontal rail striking through the totals row), the corpus tree (thumb
// over the row text and the S1 badges) and a story page (rail over the card
// panels' right edge).
//
// This is the SAME defect the transcript minimap shipped and fixed on
// 2026-09-03 (helm-bridge styles.css, "BUG 3": hiding the native bar removed
// the gutter, so the rail overlapped right-aligned bubbles; the fix reserved
// `padding-right: 24px` = 6 offset + 12 width + 6 breathing on the scroll
// host). PR-3c generalized the minimap's VISUAL treatment to ~86 panes and
// both grid lanes and did not carry its GUTTER — the twin of a fix, missed.
//
// GUTTER = 2 * edge + thickness (24 standard, 16 narrow) — byte-for-byte the
// minimap's arithmetic, so the two lanes cannot disagree. It is reserved
// whether or not the axis is scrolling right now (the `stable` semantics
// native `scrollbar-gutter` gives a classic bar): a pane whose content grows
// must not reflow.
//
// TWO MECHANISMS, ONE CONTRACT — no content may enter the last GUTTER px of
// the surface's border box. Which one a surface gets is DECIDED, not guessed
// (railGutterPlan), because they are not equivalent:
//   TRANSPARENT BORDER — the pane lane's default, and the strong one. The
//     scrollport IS the padding box, so a transparent end-edge border shrinks
//     what the surface can paint at ALL: content that overflows on the OTHER
//     axis is clipped before the strip at every scroll position, not merely
//     at rest. The pane lane uses it wherever the pane authors no border on
//     that edge — 70 of the 83 registry panes, measured 2026-09-07.
//   PADDING — for the 13 panes that author their own `border: 1px solid …`
//     box (.modal, .ctree, .tbl-wrap, .rc-menu, .hm-diffbody, …): widening
//     that edge into a transparent gutter would DELETE the pane's own line
//     and break its radius. Padding is also the mechanism the minimap
//     precedent shipped. It moves in-flow content clear of the rail; content
//     that overflows on the other axis still crosses the strip mid-scroll,
//     exactly as a native overlay bar does, and the probe names those panes.
//
// THE GRID STRIP (owner defect 2026-09-09: "when I drag a column to narrow it
// and make it fit within the viewport, the scroll bars are still visible").
// The grid lane first shipped the border mechanism ON THE VIEWPORT ITSELF
// (`.ag-grid-viewport { border-inline-end: 24px solid transparent }`), on the
// claim that AG's layout math reads clientWidth, which a border narrows
// consistently. Measured false on ag-grid-community 36.1.0: AG sizes
// `.ag-grid-scrollable-area` to `max(columns, eGridViewport.getBoundingClientRect().width)`
// (GridBodyCtrl.getHorizontalViewportWidth → updateContainerWidth) — the
// BORDER-BOX width — while `clientWidth` and its flex/column math
// (`_getInnerWidth`) exclude the border. In a 598px-wide viewport with the
// border: clientWidth 574, scrollable area 598px, scrollWidth 598 with the
// columns summing to 550 → a permanent 24px horizontal overflow, so the
// horizontal rail could NEVER turn off once the columns fit, and 24px of
// dead scroll range sat after the last column. Without the border, the same
// grid measured scrollWidth 598 == clientWidth 598 and the rail went off.
// So the grid lane reserves its strip on the viewport's PARENT (`.ag-root`,
// padding-inline-end/-block-end = GUTTER, border-box): the viewport's
// border-box IS its content box, AG's two measurements agree, and the strong
// property survives — the scrollport simply ends where the strip begins, so
// no column and no sticky pinned row can paint under either rail at any
// scroll position. The rails place themselves in that strip from the
// MEASURED gap between the viewport's end edge and its parent's (update()
// below), never from the constant, so a grid whose rule did not apply still
// gets a rail (inside the viewport, the pre-gutter placement) rather than
// one drawn 24px off into the wrapper border.
export const RAIL_GUTTER_STANDARD = 2 * RAIL_EDGE + RAIL_THICKNESS; // 24

/** The gutter a rail of `variant` needs: the rail's own strip (`edge +
 * thickness`) plus the same `edge` again as breathing, exactly as the
 * minimap's 24px reserves 6 + 12 + 6. Exported so both the gate and the CSS
 * are generated from ONE number. */
export function railGutter(variant: RailVariant): number {
  const V = RAIL_VARIANTS[variant];
  return 2 * V.edge + V.thickness;
}

/** One copy of the rail CSS, injected once per document. Geometry mirrors
 * `.pi-minimap` (helm-bridge styles.css) — change them TOGETHER (exported
 * so grid-scroll-rails.test.mjs can enforce exactly that). Colors resolve
 * at the rail element: `--sb-rail-*` where a client maps them (the console
 * maps --hc-fg/--hc-accent), else the Helm tokens directly. */
export const RAIL_CSS = `
.agr-host { position: relative; }
.agr-host .ag-body-horizontal-scroll, .agr-host .ag-body-vertical-scroll { display: none !important; }
.agr-host ${SEL_VIEWPORT} { overscroll-behavior: none; }
.agr-host ${SEL_ROOT} {
  /* the grid lane's GUTTER: PADDING ON THE VIEWPORT'S PARENT, never a border
     or padding on the viewport itself — AG 36 sizes its scrollable area to
     the viewport's border-box width while clientWidth excludes the border,
     so a viewport border was a permanent 24px overflow (THE GRID STRIP note
     above). The viewport ends where the strip begins: no column and no
     sticky pinned row can paint under either rail at any scroll position. */
  padding-inline-end: ${RAIL_GUTTER_STANDARD}px;
  padding-block-end: ${RAIL_GUTTER_STANDARD}px;
  box-sizing: border-box;
}
.agr-rail {
  position: absolute; z-index: 6; display: none; border-radius: 6px;
  cursor: pointer; touch-action: none; user-select: none;
  background: color-mix(in srgb, var(--sb-rail-ink, var(--ink, #8a93a3)) 6%, transparent);
}
.agr-rail.on { display: block; }
.agr-rail:hover, .agr-rail.dragging {
  background: color-mix(in srgb, var(--sb-rail-ink, var(--ink, #8a93a3)) 10%, transparent);
}
.agr-rail.agr-v { width: ${RAIL_THICKNESS}px; }
.agr-rail.agr-h { height: ${RAIL_THICKNESS}px; }
.agr-thumb {
  position: absolute; border-radius: 5px;
  background: color-mix(in srgb, var(--sb-rail-accent, var(--accent, #5b8fd6)) 55%, transparent);
}
.agr-v .agr-thumb { left: 1px; right: 1px; min-height: 14px; }
.agr-h .agr-thumb { top: 1px; bottom: 1px; min-width: 14px; }
.agr-rail:hover .agr-thumb, .agr-rail.dragging .agr-thumb {
  background: color-mix(in srgb, var(--sb-rail-accent, var(--accent, #5b8fd6)) 80%, transparent);
}
`;

const STYLE_ID = 'agr-rail-css';

function ensureRailCss(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = RAIL_CSS;
  doc.head.appendChild(style);
}

export interface GridScrollRailsHandle {
  /** Re-measure and re-render both rails now (callers rarely need it —
   * scroll/resize/column events already drive updates). */
  refresh(): void;
  destroy(): void;
}

/** Attach the rails to one AG grid. `host` is the element createGrid was
 * given; the adapter finds the v36 viewport inside it, mirrors its scroll
 * state onto two overlay rails, and keeps them true across scrolls,
 * resizes, and column changes (pins included). Idempotent per host. */
export function attachGridScrollRails(host: HTMLElement, api: GridApiLike): GridScrollRailsHandle {
  const doc = host.ownerDocument;
  ensureRailCss(doc);
  host.classList.add('agr-host');

  const mkRail = (axis: 'v' | 'h'): { rail: HTMLElement; thumb: HTMLElement } => {
    const rail = doc.createElement('div');
    rail.className = `agr-rail agr-${axis}`;
    rail.setAttribute('aria-hidden', 'true'); // pointer affordance; keyboard scrolling stays on the grid
    const thumb = doc.createElement('div');
    thumb.className = 'agr-thumb';
    rail.appendChild(thumb);
    host.appendChild(rail);
    return { rail, thumb };
  };
  const v = mkRail('v');
  const h = mkRail('h');

  const viewport = (): HTMLElement | null => host.querySelector<HTMLElement>(SEL_VIEWPORT);

  /** Pinned (sticky) column widths from the column state — the horizontal
   * rail's sticky bands. */
  let pinsWarned = false;
  const pinnedWidths = (): { left: number; right: number } => {
    let left = 0;
    let right = 0;
    if (api.isDestroyed?.()) return { left, right };
    // FAIL SAFE, on purpose. The pins are a REFINEMENT of the horizontal
    // rail's SPAN; whether either rail is ON is decided by scrollWidth vs
    // clientWidth and needs no column state at all. Before this guard the
    // raw `api.getColumnState()` could throw INSIDE update()'s first
    // statements — aborting it before either `classList.toggle('on', …)` —
    // so one bad api froze both rails in whatever state they were last in,
    // permanently and silently: exactly the owner's 2026-09-09 symptom
    // ("I drag a column to narrow it … the scroll bars are still visible"),
    // arrived at from a different direction. Measured 2026-09-09 on the
    // console's makeGrid with AG's modules unregistered (getColumnState is a
    // MODULE api in AG 36): 29 identical throws from this line, the rail off
    // through 40px of real overflow, a hide that never retracted it in
    // 1500ms, and 6 of 7 arms red — including BOTH positive controls, which
    // is the tell that the rail was not deciding anything at all. A rail
    // that cannot answer "which columns are pinned" must still answer "does
    // the content fit", so the span degrades to unpinned and the visibility
    // contract survives. Named once per rail, never swallowed silently.
    let state: ReturnType<GridApiLike['getColumnState']>;
    try {
      state = api.getColumnState();
      if (!state?.[Symbol.iterator]) throw new TypeError('getColumnState() did not return a list');
    } catch (err) {
      if (!pinsWarned) {
        pinsWarned = true;
        host.ownerDocument.defaultView?.console?.warn(
          `scroll-rails: pinned-column widths unavailable (${String((err as Error)?.message ?? err)}) — ` +
            `the rails span the whole viewport; visibility is unaffected`,
        );
      }
      return { left, right };
    }
    for (const c of state) {
      if (c.hide) continue;
      const w = typeof c.width === 'number' ? c.width : 0;
      if (c.pinned === 'left' || c.pinned === true) left += w;
      else if (c.pinned === 'right') right += w;
    }
    return { left, right };
  };

  let raf = 0;
  const update = (): void => {
    raf = 0;
    const vp = viewport();
    if (!vp) {
      v.rail.classList.remove('on');
      h.rail.classList.remove('on');
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const vpRect = vp.getBoundingClientRect();
    const pins = pinnedWidths();
    const topBand = host.querySelector<HTMLElement>(SEL_PINNED_TOP)?.offsetHeight ?? 0;
    const bottomBand = host.querySelector<HTMLElement>(SEL_PINNED_BOTTOM)?.offsetHeight ?? 0;
    // THE STRIP, measured: the gap between the viewport's end edge and its
    // parent's (the padding RAIL_CSS reserves on .ag-root). A rail is drawn
    // centred in it — `edge` in from the strip's outer edge, as the minimap's
    // rail sits `edge` in from its host — and when there is no strip (the
    // rule did not apply) it falls back INSIDE the viewport, the pre-gutter
    // placement, rather than off into the wrapper border.
    const rootRect = (vp.parentElement ?? vp).getBoundingClientRect();
    const stripX = clamp(rootRect.right - vpRect.right, 0, RAIL_GUTTER_STANDARD);
    const stripY = clamp(rootRect.bottom - vpRect.bottom, 0, RAIL_GUTTER_STANDARD);

    // Vertical rail: in the inline-end strip, spanning only the scrolling
    // rows (inset by the sticky top/bottom row bands).
    {
      const railTop = vpRect.top - hostRect.top + topBand + RAIL_EDGE;
      const railLen = vpRect.height - topBand - bottomBand - 2 * RAIL_EDGE;
      const g = railGeom({
        viewLen: vp.clientHeight,
        contentLen: vp.scrollHeight,
        scrollPos: vp.scrollTop,
        stickyStart: topBand,
        stickyEnd: bottomBand,
        railLen,
      });
      v.rail.classList.toggle('on', !!g);
      if (g) {
        v.rail.style.top = `${railTop}px`;
        v.rail.style.height = `${railLen}px`;
        v.rail.style.right = `${hostRect.right - vpRect.right - stripX + RAIL_EDGE}px`;
        v.thumb.style.top = `${g.thumbPos}px`;
        v.thumb.style.height = `${g.thumbLen}px`;
      }
    }

    // Horizontal rail: in the block-end strip, spanning only the CENTER
    // viewport — inset by the pinned column widths on each side (the owner's
    // pinned contract). The two strips are disjoint, so the rails cannot
    // cross; only the no-strip fallback still stops clear of the corner.
    {
      const vOn = v.rail.classList.contains('on');
      const cornerGap = vOn && stripX < RAIL_THICKNESS ? RAIL_THICKNESS + 2 : 0;
      const railLeft = vpRect.left - hostRect.left + pins.left + RAIL_EDGE;
      const railLen = vpRect.width - pins.left - pins.right - 2 * RAIL_EDGE - cornerGap;
      const g = railGeom({
        viewLen: vp.clientWidth,
        contentLen: vp.scrollWidth,
        scrollPos: vp.scrollLeft,
        stickyStart: pins.left,
        stickyEnd: pins.right,
        railLen,
      });
      h.rail.classList.toggle('on', !!g);
      if (g) {
        h.rail.style.left = `${railLeft}px`;
        h.rail.style.width = `${railLen}px`;
        h.rail.style.bottom = `${hostRect.bottom - vpRect.bottom - stripY + RAIL_EDGE}px`;
        h.thumb.style.left = `${g.thumbPos}px`;
        h.thumb.style.width = `${g.thumbLen}px`;
      }
    }
  };
  const schedule = (): void => {
    if (!raf) raf = requestAnimationFrame(update);
  };

  // --- drag (the minimap's pointer-capture discipline) -------------------
  const wireDrag = (railEl: HTMLElement, thumbEl: HTMLElement, axis: 'v' | 'h'): (() => void) => {
    const onDown = (e: PointerEvent): void => {
      const vp = viewport();
      if (!vp) return;
      e.preventDefault();
      const vertical = axis === 'v';
      const railRect = railEl.getBoundingClientRect();
      const thumbRect = thumbEl.getBoundingClientRect();
      const railLen = vertical ? railRect.height : railRect.width;
      const thumbLen = vertical ? thumbRect.height : thumbRect.width;
      const range = vertical ? vp.scrollHeight - vp.clientHeight : vp.scrollWidth - vp.clientWidth;
      const pointerPos = (ev: PointerEvent): number =>
        vertical ? ev.clientY - railRect.top : ev.clientX - railRect.left;
      const apply = (thumbPos: number): void => {
        const s = scrollFromThumb(thumbPos, railLen, thumbLen, range);
        if (vertical) vp.scrollTop = s;
        else vp.scrollLeft = s;
      };
      // Grab-anywhere: on the thumb, keep the grip offset; on the track,
      // center the thumb on the pointer and drag from there (the minimap's
      // jump-then-drag behavior).
      const onThumb = e.target === thumbEl;
      const grip = onThumb
        ? pointerPos(e) - (vertical ? thumbRect.top - railRect.top : thumbRect.left - railRect.left)
        : thumbLen / 2;
      if (!onThumb) apply(pointerPos(e) - grip);
      railEl.classList.add('dragging');
      railEl.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent): void => apply(pointerPos(ev) - grip);
      const onUp = (): void => {
        railEl.classList.remove('dragging');
        railEl.removeEventListener('pointermove', onMove);
        railEl.removeEventListener('pointerup', onUp);
        railEl.removeEventListener('pointercancel', onUp);
      };
      railEl.addEventListener('pointermove', onMove);
      railEl.addEventListener('pointerup', onUp);
      railEl.addEventListener('pointercancel', onUp);
    };
    railEl.addEventListener('pointerdown', onDown);
    return () => railEl.removeEventListener('pointerdown', onDown);
  };
  const unwireV = wireDrag(v.rail, v.thumb, 'v');
  const unwireH = wireDrag(h.rail, h.thumb, 'h');

  // The rails overlay the viewport's edge, so a wheel over them would
  // otherwise be a dead strip — forward it to the real scroll container.
  const onWheel = (e: WheelEvent): void => {
    const vp = viewport();
    if (!vp) return;
    e.preventDefault();
    vp.scrollTop += e.deltaY;
    vp.scrollLeft += e.deltaX;
  };
  v.rail.addEventListener('wheel', onWheel, { passive: false });
  h.rail.addEventListener('wheel', onWheel, { passive: false });

  // --- observation --------------------------------------------------------
  const vp0 = viewport();
  const ro = new ResizeObserver(schedule);
  ro.observe(host);
  if (vp0) {
    vp0.addEventListener('scroll', schedule, { passive: true });
    ro.observe(vp0);
    const area = vp0.querySelector<HTMLElement>('.ag-grid-scrollable-area');
    if (area) ro.observe(area); // content growth (rows/columns) without a viewport resize
    // THE SETTLE (owner defect 2026-09-09: "hiding columns does not adjust
    // the scroll bars"). AG animates a hide/show/move/pin as a 200ms CSS
    // transition on every cell's `left` (`.ag-column-moving .ag-cell,
    // .ag-header-cell { transition: left .2s }`, ColumnAnimationService) and
    // fires columnVisible/displayedColumnsChanged at the START of it, so the
    // measure those events schedule reads the OLD lefts under the NEW
    // widths. Measured 2026-09-09 (ag-grid-community 36.1.0, 598px
    // viewport, hide a 150px column, flex last column): scrollWidth 748 at
    // t=0 → rail ON, 715 at 50ms, 598 == clientWidth at 300ms — and the rail
    // was still ON at 1500ms, because nothing re-measured: no scroll, no
    // column event, and the scrollable area's SIZE was 598 before and after
    // so the ResizeObserver stayed silent. transitionend/transitioncancel
    // bubble from the cells to the viewport and ARE the settle signal —
    // event-driven, not a timer. Unfiltered on purpose: one rAF-coalesced
    // re-measure per finished transition costs what one scroll event costs,
    // and a property whitelist is a list that goes stale silently.
    vp0.addEventListener('transitionend', schedule, { passive: true });
    vp0.addEventListener('transitioncancel', schedule, { passive: true });
  }
  // Column-shape changes AG announces (pins move the rail's span; the grid
  // fires these through the same api the caller handed us).
  const agEvents = ['displayedColumnsChanged', 'columnPinned', 'columnResized', 'columnVisible', 'bodyScroll'];
  const onAg = (): void => schedule();
  for (const ev of agEvents) api.addEventListener(ev, onAg);

  let destroyed = false;
  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    ro.disconnect();
    vp0?.removeEventListener('scroll', schedule);
    vp0?.removeEventListener('transitionend', schedule);
    vp0?.removeEventListener('transitioncancel', schedule);
    if (!api.isDestroyed?.()) for (const ev of agEvents) api.removeEventListener(ev, onAg);
    unwireV();
    unwireH();
    v.rail.remove();
    h.rail.remove();
  };
  api.addEventListener('gridPreDestroyed', destroy);

  schedule();
  return { refresh: update, destroy };
}

// --- the plain-element adapter (help portal panes; WORK ITEM 62 polish) ---
//
// Same rails, same geometry (railGeom/scrollFromThumb — the R7-pinned
// treatment), no grid: `el` IS the scroll container. The extra style block
// is SEPARATE from RAIL_CSS on purpose — RAIL_CSS is twin-guarded against
// .pi-minimap by grid-scroll-rails.test.mjs and must not grow lanes that
// block has no twin for.

// THE TWO VARIANTS (Styles × Themes PR-3c). The standard rail is the
// minimap's 12px treatment — the one R7 twin-guards. `narrow` is the same
// geometry scaled for a side panel, a popover listbox or a chip strip, where
// a 12px rail eats a tenth of the pane. The values live HERE, in one place,
// and the CSS below is generated from them so a rail can never disagree with
// the math that positions it.
export const RAIL_VARIANTS = {
  standard: { thickness: 12, edge: 6, radius: 6, thumbRadius: 5, minThumb: 14 },
  narrow: { thickness: 8, edge: 4, radius: 4, thumbRadius: 3, minThumb: 12 },
} as const;
export type RailVariant = keyof typeof RAIL_VARIANTS;

const N = RAIL_VARIANTS.narrow;

const PANE_STYLE_ID = 'agr-pane-css';
export const PANE_RAIL_CSS = `
.agr-pane { scrollbar-width: none; }
.agr-pane::-webkit-scrollbar { display: none; width: 0; height: 0; }
.agr-rail.agr-narrow { border-radius: ${N.radius}px; }
.agr-rail.agr-narrow.agr-v { width: ${N.thickness}px; }
.agr-rail.agr-narrow.agr-h { height: ${N.thickness}px; }
.agr-rail.agr-narrow .agr-thumb { border-radius: ${N.thumbRadius}px; }
.agr-narrow.agr-v .agr-thumb { min-height: ${N.minThumb}px; }
.agr-narrow.agr-h .agr-thumb { min-width: ${N.minThumb}px; }
`;

/** Does this computed `overflow-*` value mean the axis can grow a rail? */
const scrollableOverflow = (v: string): boolean => v === 'auto' || v === 'scroll' || v === 'overlay';

/** A border edge that actually draws a line (and would therefore be DELETED
 *  by widening it into a transparent gutter). */
const drawsALine = (width: string, style: string): boolean =>
  Number.parseFloat(width) > 0 && style !== 'none' && style !== 'hidden';

/** What one axis of one pane did — the sweeper totals these, the probe prints
 *  them, and `railGutterPlan` is the DOM-free copy the unit gate pins. */
export interface RailGutterAxisPlan {
  axis: 'inline-end' | 'block-end';
  /** 'border' shrinks the SCROLLPORT (no content at any scroll position can
   *  reach the strip); 'padding' only moves in-flow content (an authored
   *  border on this edge would be deleted by the border lane); 'none' means
   *  the axis cannot scroll or is already clear. */
  how: 'border' | 'padding' | 'none';
  px: number;
  why: string;
}

/**
 * The gutter DECISION for one axis — pure, so grid-scroll-rails.test.mjs pins
 * it without a DOM. See the gutter note near RAIL_GUTTER_STANDARD.
 *
 * Keyed on the pane's COMPUTED overflow, never on whether the axis happens to
 * be scrolling right now: `overflow-y: auto|scroll` can grow the vertical
 * rail, so the inline-end gutter is reserved before the content grows and
 * nothing reflows when it does (native `scrollbar-gutter: stable` semantics).
 */
export function railGutterPlan(
  axis: 'inline-end' | 'block-end',
  gutter: number,
  a: { overflow: string; borderWidth: string; borderStyle: string; padding: string },
): RailGutterAxisPlan {
  if (!scrollableOverflow(a.overflow)) return { axis, how: 'none', px: 0, why: `overflow ${a.overflow} — this axis grows no rail` };
  if (drawsALine(a.borderWidth, a.borderStyle)) {
    const authored = Number.parseFloat(a.padding);
    if (Number.isFinite(authored) && authored >= gutter) {
      return { axis, how: 'none', px: authored, why: `authored padding ${authored}px already clears the rail` };
    }
    // `max(authored, gutter)`, never `authored + gutter`: a pane that already
    // holds its content off the edge must not be padded twice.
    return { axis, how: 'padding', px: gutter, why: `authored ${a.borderWidth} ${a.borderStyle} border — widening it would delete the pane's own line` };
  }
  return { axis, how: 'border', px: gutter, why: 'no authored border on this edge — a transparent one shrinks the scrollport itself' };
}

interface RailGutterKeeper {
  /** Re-decide both axes. Cheap and idempotent: it only touches the DOM when
   *  a plan actually changed. Called on every rail re-measure, because a
   *  MEDIA QUERY can turn an axis scrollable long after attach — `.railnav`
   *  is the live case (no overflow on the desktop vertical rail; the phone
   *  bottom tab bar x-scrolls at <=640px), and a gutter decided once at boot
   *  would leave the phone bar's chips under the rail. */
  sync(): void;
  release(): void;
}

/** Reserve (and keep reserving) the rail's GUTTER on one pane. */
function railGutterKeeper(el: HTMLElement, variant: RailVariant): RailGutterKeeper {
  const win = el.ownerDocument.defaultView;
  if (!win?.getComputedStyle) return { sync: () => {}, release: () => {} };
  const gutter = railGutter(variant);
  // The AUTHORED box, snapshotted ONCE, before anything of ours is inline:
  // re-reading it later would read our own gutter back and compound it.
  const cs0 = win.getComputedStyle(el);
  const authored = {
    'inline-end': {
      borderWidth: cs0.borderInlineEndWidth || cs0.borderRightWidth,
      borderStyle: cs0.borderInlineEndStyle || cs0.borderRightStyle,
      padding: cs0.paddingInlineEnd || cs0.paddingRight,
    },
    'block-end': {
      borderWidth: cs0.borderBlockEndWidth || cs0.borderBottomWidth,
      borderStyle: cs0.borderBlockEndStyle || cs0.borderBottomStyle,
      padding: cs0.paddingBlockEnd || cs0.paddingBottom,
    },
  } as const;
  const boxSizing0 = cs0.boxSizing;
  const inline0 = new Map<string, { value: string; priority: string }>();
  const remember = (prop: string): void => {
    if (!inline0.has(prop)) inline0.set(prop, { value: el.style.getPropertyValue(prop), priority: el.style.getPropertyPriority(prop) });
  };
  const restore = (prop: string): void => {
    const was = inline0.get(prop);
    if (was?.value) el.style.setProperty(prop, was.value, was.priority);
    else el.style.removeProperty(prop);
  };
  const applied: Record<string, RailGutterAxisPlan['how']> = { 'inline-end': 'none', 'block-end': 'none' };

  const sync = (): void => {
    const cs = win.getComputedStyle(el);
    const plans = [
      railGutterPlan('inline-end', gutter, { overflow: cs.overflowY, ...authored['inline-end'] }),
      railGutterPlan('block-end', gutter, { overflow: cs.overflowX, ...authored['block-end'] }),
    ];
    let changed = false;
    for (const p of plans) {
      if (applied[p.axis] === p.how) continue;
      changed = true;
      remember(`border-${p.axis}`);
      remember(`padding-${p.axis}`);
      restore(`border-${p.axis}`);
      restore(`padding-${p.axis}`);
      if (p.how === 'border') el.style.setProperty(`border-${p.axis}`, `${p.px}px solid transparent`);
      else if (p.how === 'padding') el.style.setProperty(`padding-${p.axis}`, `${p.px}px`);
      applied[p.axis] = p.how;
    }
    // Stamped every time, changed or not: the probe and any operator reading
    // the DOM must be able to tell "no gutter needed here" from "the keeper
    // never ran" — an unstamped pane is a silent failure, not a decision.
    el.dataset.railGutter = plans.filter((p) => p.how !== 'none').map((p) => `${p.axis}:${p.how}:${p.px}`).join(' ') || 'none';
    if (!changed) return;
    // `box-sizing: border-box` is the app-wide floor in both clients, so a
    // gutter shrinks the content box instead of growing the pane. Stamp it
    // anyway — a pane that opted out would otherwise widen by the gutter.
    if (boxSizing0 !== 'border-box' && plans.some((p) => p.how !== 'none')) {
      remember('box-sizing');
      el.style.setProperty('box-sizing', 'border-box');
    }
  };
  sync();
  return {
    sync,
    release(): void {
      for (const prop of inline0.keys()) restore(prop);
      delete el.dataset.railGutter;
    },
  };
}

function ensurePaneCss(doc: Document): void {
  if (doc.getElementById(PANE_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = PANE_STYLE_ID;
  style.textContent = PANE_RAIL_CSS;
  doc.head.appendChild(style);
}

/** The rail's stacking rule (S×T W2, owner screenshot 2026-09-09). The rail
 *  nodes are SIBLINGS of the pane, appended to its host, and the stylesheet
 *  gives them `z-index: 6`. A pane that is itself positioned with a larger
 *  z-index — every popover card in the product is `z-index: 40` — therefore
 *  paints OVER its own rail: on the Helm's "Show columns" picker the list
 *  clipped at "Next action" with no visible scrollbar, and the console's
 *  picker measured `rail z 6 vs menu z 40, topmost #hcColsMenu` at the
 *  rail's midpoint while the probe's "rail drawn" line (the `on` class) read
 *  green. The fix is the primitive's, never per site: the rail rides one
 *  above the pane's own z-index whenever that would otherwise bury it.
 *  Pure so the unit suite can prove both directions without a browser. */
export const RAIL_BASE_Z = 6;
export function railZIndexFor(paneZ: string | number | null | undefined, base = RAIL_BASE_Z): number | null {
  const z = typeof paneZ === 'number' ? paneZ : Number.parseInt(String(paneZ ?? ''), 10);
  if (!Number.isFinite(z)) return null;      // `auto` — the sheet's base applies
  return z >= base ? z + 1 : null;           // below the base: the sheet's 6 already wins
}

export interface ElementScrollRailHandle {
  refresh(): void;
  destroy(): void;
}

/** Attach overlay rails to a PLAIN scrolling element (an overflow pane).
 * `host` (default: el's parent) becomes the positioning context and holds
 * the rail nodes; `el`'s native scrollbar is hidden (.agr-pane). Content
 * mutations re-measure (a docs pane grows after async renders — a grid
 * announces those through its api; a plain pane needs the observer).
 *
 * `variant` picks the treatment: 'standard' (the minimap's 12px rail, the
 * default and the R7-guarded one) or 'narrow' (8px, for side panels,
 * popover listboxes and chip strips).
 *
 * HOST POSITIONING (PR-3c): the grid lane stamps `.agr-host` unconditionally,
 * which is safe there because a grid host is always a plain static wrapper.
 * The sweeper below attaches to ~86 arbitrary panes, and stamping
 * `position: relative` on a parent that was `absolute`/`fixed`/`sticky` would
 * MOVE it — so this lane only promotes a host whose computed position is
 * `static`, and does it inline so the class's blanket rule cannot win. */
export function attachElementScrollRail(
  el: HTMLElement,
  opts: { host?: HTMLElement; variant?: RailVariant } = {},
): ElementScrollRailHandle {
  const host = opts.host ?? (el.parentElement as HTMLElement | null);
  if (!host) throw new Error('attachElementScrollRail: el has no host/parent to hold the rails');
  const doc = el.ownerDocument;
  const variant: RailVariant = opts.variant ?? 'standard';
  const V = RAIL_VARIANTS[variant];
  ensureRailCss(doc);
  ensurePaneCss(doc);
  const win = doc.defaultView;
  const already = win?.getComputedStyle ? win.getComputedStyle(host).position : 'static';
  if (!already || already === 'static') host.style.position = 'relative';
  el.classList.add('agr-pane');
  // The gutter is reserved AFTER `.agr-pane` lands (that class is what hides
  // the native bar, so the computed padding read below is the one the pane
  // will actually render with) and BEFORE the first measure.
  const gutterKeeper = railGutterKeeper(el, variant);

  const mkRail = (axis: 'v' | 'h'): { rail: HTMLElement; thumb: HTMLElement } => {
    const rail = doc.createElement('div');
    rail.className = `agr-rail agr-${axis}${variant === 'narrow' ? ' agr-narrow' : ''}`;
    rail.setAttribute('aria-hidden', 'true');
    const thumb = doc.createElement('div');
    thumb.className = 'agr-thumb';
    rail.appendChild(thumb);
    host.appendChild(rail);
    return { rail, thumb };
  };
  const v = mkRail('v');
  const h = mkRail('h');

  let raf = 0;
  let backstop: ReturnType<typeof setTimeout> | null = null;
  const update = (): void => {
    raf = 0;
    if (backstop) {
      clearTimeout(backstop);
      backstop = null;
    }
    // A media query can turn an axis scrollable after attach; re-decide
    // before measuring so the rail is never drawn over a pane that just
    // grew a scrollbar without a gutter.
    gutterKeeper.sync();
    // Paint order: the rails are the pane's siblings, so a positioned pane
    // with a z-index above the rail's base draws over them (railZIndexFor).
    // Re-read every pass — the sheet sets a popover's z, but a site can lift
    // it at open.
    {
      const paneZ = win?.getComputedStyle ? win.getComputedStyle(el).zIndex : 'auto';
      const lift = railZIndexFor(paneZ);
      const zs = lift === null ? '' : String(lift);
      if (v.rail.style.zIndex !== zs) v.rail.style.zIndex = zs;
      if (h.rail.style.zIndex !== zs) h.rail.style.zIndex = zs;
    }
    const hostRect = host.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    {
      const railTop = elRect.top - hostRect.top + V.edge;
      const railLen = elRect.height - 2 * V.edge;
      const g = railGeom({
        viewLen: el.clientHeight, contentLen: el.scrollHeight, scrollPos: el.scrollTop,
        stickyStart: 0, stickyEnd: 0, railLen, minThumb: V.minThumb,
      });
      v.rail.classList.toggle('on', !!g);
      if (g) {
        v.rail.style.top = `${railTop}px`;
        v.rail.style.height = `${railLen}px`;
        v.rail.style.right = `${hostRect.right - elRect.right + V.edge}px`;
        v.thumb.style.top = `${g.thumbPos}px`;
        v.thumb.style.height = `${g.thumbLen}px`;
      }
    }
    {
      const vOn = v.rail.classList.contains('on');
      const cornerGap = vOn ? V.thickness + 2 : 0;
      const railLeft = elRect.left - hostRect.left + V.edge;
      const railLen = elRect.width - 2 * V.edge - cornerGap;
      const g = railGeom({
        viewLen: el.clientWidth, contentLen: el.scrollWidth, scrollPos: el.scrollLeft,
        stickyStart: 0, stickyEnd: 0, railLen, minThumb: V.minThumb,
      });
      h.rail.classList.toggle('on', !!g);
      if (g) {
        h.rail.style.left = `${railLeft}px`;
        h.rail.style.width = `${railLen}px`;
        h.rail.style.bottom = `${hostRect.bottom - elRect.bottom + V.edge}px`;
        h.thumb.style.left = `${g.thumbPos}px`;
        h.thumb.style.width = `${g.thumbLen}px`;
      }
    }
  };
  // rAF with a timeout backstop: rAF starves in hidden documents (a
  // background tab, a headless page), and a MutationObserver-scheduled
  // re-measure that waits on a frame that never comes leaves a stale rail
  // — measured in kb-layout-probes (the article pane's rail stayed off
  // after its content landed under --dump-dom). Whichever fires first
  // cancels the other.
  const schedule = (): void => {
    if (raf || backstop) return;
    raf = requestAnimationFrame(update);
    backstop = setTimeout(() => {
      backstop = null;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      update();
    }, 64);
  };

  const wireDrag = (railEl: HTMLElement, thumbEl: HTMLElement, axis: 'v' | 'h'): (() => void) => {
    const onDown = (e: PointerEvent): void => {
      e.preventDefault();
      const vertical = axis === 'v';
      const railRect = railEl.getBoundingClientRect();
      const thumbRect = thumbEl.getBoundingClientRect();
      const railLen = vertical ? railRect.height : railRect.width;
      const thumbLen = vertical ? thumbRect.height : thumbRect.width;
      const range = vertical ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
      const pointerPos = (ev: PointerEvent): number =>
        vertical ? ev.clientY - railRect.top : ev.clientX - railRect.left;
      const apply = (thumbPos: number): void => {
        const s = scrollFromThumb(thumbPos, railLen, thumbLen, range);
        if (vertical) el.scrollTop = s;
        else el.scrollLeft = s;
      };
      const onThumb = e.target === thumbEl;
      const grip = onThumb
        ? pointerPos(e) - (vertical ? thumbRect.top - railRect.top : thumbRect.left - railRect.left)
        : thumbLen / 2;
      if (!onThumb) apply(pointerPos(e) - grip);
      railEl.classList.add('dragging');
      railEl.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent): void => apply(pointerPos(ev) - grip);
      const onUp = (): void => {
        railEl.classList.remove('dragging');
        railEl.removeEventListener('pointermove', onMove);
        railEl.removeEventListener('pointerup', onUp);
        railEl.removeEventListener('pointercancel', onUp);
      };
      railEl.addEventListener('pointermove', onMove);
      railEl.addEventListener('pointerup', onUp);
      railEl.addEventListener('pointercancel', onUp);
    };
    railEl.addEventListener('pointerdown', onDown);
    return () => railEl.removeEventListener('pointerdown', onDown);
  };
  const unwireV = wireDrag(v.rail, v.thumb, 'v');
  const unwireH = wireDrag(h.rail, h.thumb, 'h');

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    el.scrollTop += e.deltaY;
    el.scrollLeft += e.deltaX;
  };
  v.rail.addEventListener('wheel', onWheel, { passive: false });
  h.rail.addEventListener('wheel', onWheel, { passive: false });

  el.addEventListener('scroll', schedule, { passive: true });
  const ro = new ResizeObserver(schedule);
  ro.observe(host);
  ro.observe(el);
  const mo = new MutationObserver(schedule);
  mo.observe(el, { childList: true, subtree: true });

  let destroyed = false;
  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    if (backstop) clearTimeout(backstop);
    ro.disconnect();
    mo.disconnect();
    el.removeEventListener('scroll', schedule);
    unwireV();
    unwireH();
    v.rail.remove();
    h.rail.remove();
    gutterKeeper.release();
    el.classList.remove('agr-pane');
  };

  schedule();
  return { refresh: update, destroy };
}

// --- the REGISTRY SWEEPER (Styles × Themes PR-3c) -------------------------
//
// The owner ruling bans the browser's default scrollbar on EVERY scrollable
// surface, and WF-A measured ~86 of them across the five clients. Hand-wiring
// 86 `attachElementScrollRail` calls would have put the contract in 86 places
// and left every future overflow rule bare by default — the shape of debt
// this program exists to remove.
//
// So the surfaces declare a REGISTRY (one `RailSpec` per CSS selector, living
// in the surface that owns the selector) and this sweeper attaches the rail
// wherever a match appears, now or later. That makes the contract
// MEASURABLE: primitives-nav-check parses each stylesheet's `overflow:
// auto|scroll|overlay` rules and refuses any selector the registry does not
// cover, naming the file:line — a new scrollable surface cannot ship bare.
//
// A pane is railed AT MOST ONCE: the WeakMap catches ours, the `.agr-pane`
// class catches a hand-wired call site (the portal's three, the console's
// four), so a selector may safely appear in both lanes.

export interface RailSpec {
  /** CSS selector for the SCROLLING element (the one carrying overflow). */
  sel: string;
  /** 'narrow' (8px — side panels, popovers, chip strips) or 'standard'. */
  variant: RailVariant;
  /** Optional selector for the positioning host, resolved with `closest()`
   *  from the pane. Default: the pane's parent element. Name one when the
   *  parent is itself clipped (a rail drawn inside an `overflow:hidden`
   *  parent is invisible). */
  host?: string;
  /** Why this surface rides this variant. Documentation the gate prints. */
  note?: string;
}

export interface ScrollRailSweeper {
  /** Attach to anything that has appeared since the last sweep. Returns the
   *  number of panes newly railed — the measured count, never a bare "ok". */
  sweep(): number;
  /** Panes currently railed by THIS sweeper. */
  count(): number;
  destroy(): void;
}

/** Opt out of the sweep on one element: `data-rail="off"`. Used for a pane
 *  that carries its own instrument (the transcript minimap) or one whose
 *  overflow is a safety valve that must never draw a bar. */
const OPT_OUT = '[data-rail="off"]';

/**
 * Install the registry sweeper on a document. Call once per client at boot,
 * beside `installConfirmDestructive()`; the returned handle uninstalls.
 *
 * Late-mounted surfaces are the norm here (every dialog, popover and menu in
 * this codebase is built on open), so a one-shot sweep would rail the shell
 * and nothing else. A MutationObserver re-sweeps on DOM growth, coalesced
 * through rAF with a timeout backstop — rAF starves in a hidden document and
 * a sweep that waits on a frame that never comes is the stale-rail defect
 * kb-layout-probes already caught once in the pane lane.
 */
export function installScrollRails(doc: Document, specs: readonly RailSpec[]): ScrollRailSweeper {
  const railed = new WeakMap<Element, ElementScrollRailHandle>();
  let live = 0;
  let destroyed = false;

  const sweep = (): number => {
    if (destroyed || typeof doc.querySelectorAll !== 'function') return 0;
    let added = 0;
    for (const spec of specs) {
      let matches: ArrayLike<Element>;
      try {
        matches = doc.querySelectorAll(spec.sel);
      } catch {
        continue; // a selector this engine does not understand is not a crash
      }
      for (let i = 0; i < matches.length; i++) {
        const el = matches[i] as HTMLElement;
        if (railed.has(el)) continue;
        // `.agr-pane` marks a pane a hand-wired call site already railed —
        // the two lanes must never double-draw.
        if (el.classList?.contains('agr-pane')) continue;
        if (el.closest?.(OPT_OUT)) continue;
        const host = spec.host ? (el.closest(spec.host) as HTMLElement | null) : el.parentElement;
        if (!host || host === el) continue;
        try {
          railed.set(el, attachElementScrollRail(el, { host, variant: spec.variant }));
          live++;
          added++;
        } catch {
          // A pane that cannot be railed (detached, no host) must not take
          // the rest of the sweep down with it.
        }
      }
    }
    return added;
  };

  let raf = 0;
  let backstop: ReturnType<typeof setTimeout> | null = null;
  const schedule = (): void => {
    if (destroyed || raf || backstop) return;
    const run = (): void => {
      raf = 0;
      if (backstop) { clearTimeout(backstop); backstop = null; }
      sweep();
    };
    raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : 0;
    backstop = setTimeout(() => {
      backstop = null;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      sweep();
    }, 120);
  };

  let mo: MutationObserver | null = null;
  if (typeof MutationObserver === 'function' && doc.body) {
    mo = new MutationObserver(schedule);
    mo.observe(doc.body, { childList: true, subtree: true });
  }
  sweep();

  return {
    sweep,
    count: () => live,
    destroy(): void {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      if (backstop) clearTimeout(backstop);
      mo?.disconnect();
    },
  };
}
